import * as React from 'react';
import isNil from 'lodash/isNil';

import CreatorInfoWithChannels from 'components/creator-info-with-channels/CreatorInfoWithChannels';
import ClientApprovalEntry from '../client-approval-entry/ClientApprovalEntry';

import type { ChannelMetrics, Channels, ClientApprovalBucketV2, ClientApprovalStatus } from 'types';

import './client-approval-group.scss';

type Props = {
  groupItems: Array<ClientApprovalBucketV2>,
  onAddNote: (bucketId: number, note: string) => void,
  onEntryDownload: (bucketId: number, look: string) => void,
  onMessagesChecked: (bucketId: number, ids: Array<number>) => void,
  onCaptionStatusChange: (bucketId: number, status: ClientApprovalStatus) => void,
  onAssetApprovalChange: (bucketId: number, itemId: number, status: ClientApprovalStatus) => void
};

function extractMetrics(channels: Channels): ChannelMetrics {
  return Object.keys(channels)
    .reduce<ChannelMetrics>((result, key) => {
      const channelData = channels[key];
      if (!channelData) {
        return result;
      }

      return {
        ...result,
        [key]: channelData.metrics
      };
    }, {});
}

const ClientApprovalGroup = (
  {
    groupItems,
    onAddNote,
    onEntryDownload,
    onMessagesChecked,
    onCaptionStatusChange,
    onAssetApprovalChange
  }: Props
): JSX.Element => {
  const mappedByCreator = new Map();

  // use Map() to maintain original insertion order
  groupItems.forEach(item => {
    const influencerId = item.influencerProfile?.id;
    const updatedItems = !isNil(mappedByCreator.get(influencerId))
      ? mappedByCreator.get(influencerId).concat([item])
      : [item];

    mappedByCreator.set(influencerId, updatedItems);
  });

  return (
    <section className="client-approval-group">
      <div className="client-approval-group__title">
        {groupItems[0].look}
      </div>
      {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [...mappedByCreator].map(([key, group]) => {
          const profile = group[0]?.influencerProfile;
          if (!profile) {
            return null;
          }

          return (
            <div
              key={`approval-group-${profile.id}`}
              className="client-approval-group__creator-section"
            >
              <CreatorInfoWithChannels
                omitLinks
                includeRatio
                creatorProfile={profile}
                metrics={extractMetrics(profile.channels)}
              />
              {
                group.map(item => (
                  <ClientApprovalEntry
                    key={`approval-entry-${item.id}`}
                    item={item}
                    onAddNote={(note) => onAddNote(item.id, note)}
                    onEntryDownload={() => onEntryDownload(item.id, item.look)}
                    onMessagesChecked={(ids) => onMessagesChecked(item.id, ids)}
                    onCaptionStatusChange={(status) => onCaptionStatusChange(item.id, status)}
                    onAssetApprovalChange={(itemId, status) => onAssetApprovalChange(item.id, itemId, status)}
                  />
                ))
              }
            </div>
          );
        })
      }
    </section>
  );
};

export default ClientApprovalGroup;
